import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DateUtils,
  IButton,
  ToasterService,
  TranslateService,
} from '@devinforius/i-essentials';
import { IFileAction } from '@devinforius/i-file';
import { IMap } from '@devinforius/i-map';
import { environment } from '@environments/environment';
import {
  ConstatationDetail,
  ConstatationDevice,
} from '@shared/models/constatation';
import { ConstatationService } from '@shared/services/constatation.service';
import { catchError, EMPTY, map, Observable, switchMap } from 'rxjs';

@Component({
  selector: 'app-constatation-details',
  standalone: false,
  templateUrl: './constatation-details.component.html',
  styleUrl: './constatation-details.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ConstatationDetailsComponent implements OnInit {
  @HostListener('document:keydown.arrowleft', ['$event'])
  goToPreviouysConstatation(evt: Event): void {
    if (this.canNavigate) {
      evt.preventDefault();
      this.goTo('previous');
    }
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  goToNextConstatation(evt: Event): void {
    if (this.canNavigate) {
      evt.preventDefault();
      this.goTo('next');
    }
  }

  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private toaster: ToasterService = inject(ToasterService);
  private translater: TranslateService = inject(TranslateService);
  private constatationService: ConstatationService =
    inject(ConstatationService);

  public titleButtons!: IButton[];
  public form!: FormGroup;
  public currentConstatation!: ConstatationDetail;
  public iMapToken = environment.iMapToken;
  public loading: boolean = false;
  public showFilesModal: boolean = false;
  public showRefusalModal: boolean = false;
  public loadingModal: boolean = false;

  // URL de la photo affichée dans la modale de visualisation (null = fermée).
  // La modale elle-même est le composant <app-picture-viewer>.
  public zoomedPicture: string | null = null;

  public ngOnInit(): void {
    // La route `constatations/:id` réutilise le composant : paramMap réémet à
    // chaque changement d'id (navigation précédent/suivant) sans repasser par ngOnInit.
    this.route.paramMap
      .pipe(
        map((params) => params.get('id') ?? ''),
        // switchMap annule le GET en cours à chaque changement d'id. Avec des
        // souscriptions indépendantes, deux requêtes concurrentes (navigation
        // ←/→ rapide) peuvent répondre dans le désordre et laisser affichée une
        // constatation qui ne correspond plus à l'id de l'URL.
        switchMap((id) => this.getConstatation(id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((constatation) => this.setConstatation(constatation));
  }

  public back(): void {
    this.router.navigate(['constatations']);
  }

  public goTo(direction: 'next' | 'previous') {
    const id =
      direction === 'next'
        ? this.currentConstatation.nextOffenceId
        : this.currentConstatation.previousOffenceId;

    this.router.navigate(['constatations', id]);
  }

  public getControl(key: string): FormControl {
    return this.form?.get(key) as FormControl;
  }

  public openPicture(url: string): void {
    this.zoomedPicture = url;
  }

  public send(action: 'validate' | 'refuse'): void {
    if (action === 'refuse') {
      this.showRefusalModal = true;
    } else if (action === 'validate') {
      this.validateConstatation();
    }
  }

  public confirmRefusal(refusalReason: string): void {
    this.abortConstatation(refusalReason);
  }

  public get canNavigate(): boolean {
    return !this.showFilesModal && !this.showRefusalModal && !this.zoomedPicture;
  }

  public get operatorFullName(): string {
    return `${this.currentConstatation.operator?.firstName || '-'} ${
      this.currentConstatation.operator?.lastName || '-'
    }`;
  }

  public get operatorDate(): string {
    return this.currentConstatation.operatorDate
      ? DateUtils.format(this.currentConstatation.operatorDate, 'DD/MM/YYYY')
      : '-';
  }

  public showAllChange(showAll: boolean): void {
    setTimeout(() => {
      this.currentConstatation.devices.forEach((device) => {
        device.showMap = showAll;
      });
    }, 1000);
  }

  public toggleAccordion(device: ConstatationDevice): void {
    setTimeout(() => {
      device.showMap = !device.showMap;
    }, 500);
  }

  public getCustomAction(path: string): IFileAction {
    return {
      icon: 'bi bi-download',
      click: () => {
        window.open(path, 'blank');
      },
    };
  }

  private getConstatation(id: string): Observable<ConstatationDetail> {
    const params: { date?: string; stateId?: number; search?: string } = {};
    this.loading = true;

    if (sessionStorage.getItem('current-search')) {
      const search = JSON.parse(
        sessionStorage.getItem('current-search') ||
          "{ type: 'date', value: this.today }",
      );

      if (search.type === 'date') {
        params['date'] = DateUtils.format(search.value as string, 'YYYY-MM-DD');
      }

      if (search.type === 'state') {
        params['stateId'] = 1; // Pour l'instant le filtre ne se fait que sur les constatations à traiter
      }

      if (search.quickSearch) {
        params['search'] = search.quickSearch;
      }
    }

    return this.constatationService.get(id).pipe(
      catchError(() => {
        this.loading = false;
        this.toaster.push(
          'error',
          this.translater.translate('global.error'),
          this.translater.translate('details.error.get'),
        );

        // Sans constatation le détail n'affiche rien : on renvoie sur la liste.
        this.back();

        // EMPTY et non throwError : le flux des changements d'id doit survivre
        // à une constatation en erreur.
        return EMPTY;
      }),
    );
  }

  private setConstatation(constatation: ConstatationDetail): void {
    this.currentConstatation = constatation;

    this.currentConstatation.devices.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    this.currentConstatation.devices = this.currentConstatation.devices.map(
      (value, index) => ({
        ...value,
        accordionTitle: `${this.translater.translate('details.camera')} ${
          index + 1
        } : ${DateUtils.format(value.date, 'DD/MM/YYYY à HH:mm')}`,
        showMap: false,
        coordinates: {
          latitude: value.latitude,
          longitude: value.longitude,
        },
        iMapConfig: new IMap({
          zoom: 15,
          center: [value.longitude, value.latitude],
          showControls: false,
          showFullScreen: false,
          showGeolocate: false,
          showScaleControls: false,
        }),
      }),
    );

    this.initData();
    this.initForm();
    this.loading = false;
  }

  private initData(): void {
    const previousButton: IButton = {
      text: this.translater.translate('global.previous'),
      icon: 'bi-arrow-left-circle-fill',
      iconPosition: 'left',
      styleClass: 'custom',
      background: '#23344b',
      color: '#FFF',
      click: () => this.goTo('previous'),
    };

    const nextButton: IButton = {
      text: this.translater.translate('global.next'),
      icon: 'bi-arrow-right-circle-fill',
      iconPosition: 'right',
      background: '#23344b',
      color: '#FFF',
      click: () => this.goTo('next'),
    };

    const buttons: IButton[] = [];

    if (this.currentConstatation?.previousOffenceId) {
      buttons.push(previousButton);
    }

    if (this.currentConstatation?.nextOffenceId) {
      buttons.push(nextButton);
    }

    this.titleButtons = buttons;
  }

  private initForm(): void {
    this.form = new FormGroup({
      plate: new FormControl(
        this.currentConstatation?.plate ?? '',
        Validators.maxLength(50),
      ),
      plateCountryCode: new FormControl(
        this.currentConstatation?.plateCountryCode ?? '',
        Validators.maxLength(50),
      ),
      vehicleCategory: new FormControl(
        this.currentConstatation?.vehicleCategory ?? '',
        Validators.maxLength(125),
      ),
      offenceId: new FormControl(
        this.currentConstatation?.offenceId ?? '',
        Validators.maxLength(255),
      ),
      offenceType: new FormControl(
        this.currentConstatation?.offenceType ?? '',
        Validators.maxLength(255),
      ),
      photoType: new FormControl(
        this.currentConstatation?.photoType ?? '',
        Validators.maxLength(255),
      ),
      operatorComment: new FormControl(
        this.currentConstatation?.operatorComment ?? '',
        Validators.maxLength(255),
      ),
    });

    if (
      this.currentConstatation.stateCode === 'CANCELED' ||
      this.currentConstatation.stateCode === 'APPROVED'
    ) {
      this.form.disable();
    }
  }

  private abortConstatation(refusalReason: string): void {
    this.loadingModal = true;

    this.constatationService
      .refuse(
        this.currentConstatation.id,
        this.form.getRawValue(),
        this.form.get('operatorComment')?.value,
        refusalReason,
      )
      .subscribe({
        next: () => {
          this.loadingModal = false;
          this.showRefusalModal = false;

          this.toaster.push(
            'success',
            this.translater.translate('global.success'),
            this.translater.translate('details.success.refuse'),
          );

          if (this.currentConstatation.nextOffenceId) {
            this.goTo('next');
          } else {
            this.back();
          }
        },
        error: () => {
          this.loadingModal = false;
          this.showRefusalModal = false;

          this.toaster.push(
            'error',
            this.translater.translate('global.error'),
            this.translater.translate('details.error.refuse'),
          );
        },
      });
  }

  private validateConstatation(): void {
    this.constatationService
      .validate(
        this.currentConstatation.id,
        this.form.getRawValue(),
        this.form.get('operatorComment')?.value
      )
      .subscribe({
        next: () => {
          this.loadingModal = false;

          this.toaster.push(
            'success',
            this.translater.translate('global.success'),
            this.translater.translate('details.success.validate'),
          );

          if (this.currentConstatation.nextOffenceId) {
            this.goTo('next');
          } else {
            this.back();
          }
        },
        error: () => {
          this.loadingModal = false;
          this.toaster.push(
            'error',
            this.translater.translate('global.error'),
            this.translater.translate('details.error.validate'),
          );
        },
      });
  }

  public getDevicePictures(device: ConstatationDevice): { url: string }[] {
    return device.pictures.map((picture) => ({ url: picture.path }));
  }
}
